import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { disconnect } from '@wagmi/core';
import { ToastrService } from 'ngx-toastr';
import { LoginService } from 'src/app/services/login.service';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { wagmi_config } from '../wagmi/config';
import { AppkitService } from './appkit.service';

@Injectable({
    providedIn: 'root'
})

/**
 * Idle Service for auto logout if the time(which has been set in environment) expires
 */
export class IdleService {
    private token: string = '';
    private expiryTime: string = '';
    private isLoggingOut: boolean = false;

    /**
     * constructor
     */
    constructor(
        private idle: Idle,
        private storageService: StorageService,
        private router: Router,
        private appkitService: AppkitService,
        private loginService: LoginService,
        private toastr: ToastrService
    ) {
        // this.user = this.webStorageService.getLocalStorage('user') != null ? JSON.parse(this.webStorageService.getLocalStorage('user') || 'undefined') : this.user;
        this.idle.setIdle(environment.AUTO_LOGOUT_DURATION);
        this.idle.setTimeout(environment.AUTO_LOGOUT_TIMEOUT);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.idle.onIdleEnd.subscribe(() => this.reset());

        /** Backup to stop idle timer if still running while user is not logged in (haven't seen this be needed yet) */
        this.idle.onTimeoutWarning.subscribe(() => {
            if (!this.isLoggedIn) {
                this.idle.stop();
            }
        });

        /** called after the inactive timeout */
        this.idle.onTimeout.subscribe(() => {
            // this.accountService.updateAuthentication(false);
        });
        if (this.isLoggedIn) {
            this.idle.watch();
        }
        // uncomment this after jwt expiry time is added in login response
        this.checkSessionValidity();
        this.watchWalletDisconnect();
    }

    /**
     * Detect when the wallet transitions from connected to disconnected
     * (e.g. AppKit modal "Disconnect" button) and trigger the full logout
     * flow so the server-side session is invalidated.
     */
    private watchWalletDisconnect(): void {
        let wasConnected = false;
        this.appkitService.walletState$.subscribe((state) => {
            if (wasConnected && !state.connected && this.isLoggedIn) {
                this.logout();
            }
            wasConnected = state.connected;
        });
    }

    private get isLoggedIn(): boolean {
        this.token = this.storageService.getItem('token') || '';
        return !!this.token;
    }

    private checkSessionValidity(): void {
        const expiryTimeString = this.storageService.getItem('expiryTime');
        if (expiryTimeString) {
            this.expiryTime = JSON.parse(expiryTimeString);
        } else {
            this.expiryTime = '{}'; // or set it to null or a default value
        }
        const expirationDate = this.expiryTime;

        // Ensure the token expiration date exists
        if (expirationDate) {
            const currentTime = new Date().getTime();
            const expTime = new Date(expirationDate).getTime();
            console.log(currentTime, expTime);

            // Check if the token is expired
            if (expTime <= currentTime) {
                this.handleSessionExpired();
            }
        }
    }

    /**
     * Ends an expired session: tells the user, then runs the full logout
     * (server logout, clear storage, disconnect wallet, back to the login page).
     * The toast is shown only for a live session, once per logout.
     * Uses show() with the error style rather than error(): expiry is the normal
     * session end, and error() is mirrored to the Telegram alert channel.
     * @returns {void}
     */
    public handleSessionExpired(): void {
        if (this.isLoggedIn && !this.isLoggingOut) {
            this.toastr.show(
                'Your session has expired. Please connect your wallet and sign in again.',
                'Session Expired',
                {},
                'toast-error'
            );
        }
        this.logout();
    }


    /**
     * start idle if logged in & not running.
     */
    public reset(): void {
        if (this.isLoggedIn && !this.idle.isRunning()) {
            this.idle.watch();
        }
    }

    public stop(): void {
        this.idle.stop();
    }

    /**
     * logout — invalidates the session on the server (best-effort) before
     * clearing local state. Re-entrancy guarded so a 401 from the logout
     * endpoint cannot re-trigger this flow via the HTTP interceptor.
     */
    logout() {
        if (this.isLoggingOut) {
            return;
        }
        this.isLoggingOut = true;

        const finalize = () => {
            this.storageService.clearItem();
            this.idle.stop();
            disconnect(this.appkitService.adapter.wagmiConfig);
            this.router.navigate(['/']);
            this.isLoggingOut = false;
        };

        if (this.isLoggedIn) {
            this.loginService.logout().subscribe({
                next: () => finalize(),
                error: () => finalize()
            });
        } else {
            finalize();
        }
    }


}

